// Wire types — mirror the Go DTOs exactly. When the backend changes,
// update these and the build breaks at every mismatch (that's the point).
//
// Source of truth on the Go side:
//   internal/auth/http.go     — login / me / refresh
//   internal/auth/users.go    — User
//   internal/search/domain.go — SessionResponse / SearchResponse

/** internal/auth/users.go: User */
export interface User {
  id: number;
  username: string;
  full_name: string;
  /** "admin" | "support" | "reseller" — kept as string; narrow via features/auth/roles.ts. */
  role: string;
  enabled: boolean;
  last_login?: string; // RFC3339
  /** Set only for role === "reseller": the radius_resellers.id this login is scoped to (+ name). */
  reseller_id?: number | null;
  reseller_name?: string;
  /** Per-reseller device grants (role === "reseller" only): names of the OLTs / Mikrotik
   *  routers this login may operate on as if it were a support user. Absent for admin/support
   *  (no per-device scoping — they can do everything). See features/auth/roles.ts. */
  grant_olt_names?: string[];
  grant_router_names?: string[];
}

/** POST /v1/api/auth/login body */
export interface LoginRequest {
  username: string;
  password: string;
}

/** POST /v1/api/auth/login response (internal/auth/http.go: loginResponse) */
export interface LoginResponse {
  ok: boolean;
  token?: string;
  expires_at?: string; // RFC3339 UTC
  user?: User;
  error?: string;
  retry_in?: number; // seconds, only on 429
}

/** GET /v1/api/auth/me response (meResponse) */
export interface MeResponse {
  ok: boolean;
  user?: User;
  error?: string;
}

/** POST /v1/api/auth/refresh response (refreshResponse) */
export interface RefreshResponse {
  ok: boolean;
  token?: string;
  expires_at?: string;
  error?: string;
}

/** internal/search/domain.go: SessionResponse */
export interface SubscriberSession {
  router: string;
  router_ip: string;
  username: string;
  address: string;
  uptime: string;
  caller_id: string;
  service: string;
  status: string;
  bytes_in?: number;
  bytes_out?: number;
  last_logoff?: string;
  updated_at?: string;
  vendor?: string;
}

/** GET /v1/api/search?q=… response (SearchResponse) */
export interface SearchResponse {
  ok: boolean;
  session?: SubscriberSession;
  /** "username" | "scan" | "radius" — how the match was found */
  match?: string;
  /** Pass-through ONU JSON the OLT poller wrote; shape varies by brand. */
  onu?: unknown;
  error?: string;
}

/** internal/search/domain.go: SuggestMatch — one autocomplete row. */
export interface SuggestMatch {
  username: string;
  router: string;
  switch: string;
  address: string;
  caller_id: string;
  mobile: string;
  status: string;
}

/** GET /v1/api/suggest?q=… response (SuggestResponse) */
export interface SuggestResponse {
  ok: boolean;
  matches: SuggestMatch[];
  error?: string;
}

/** internal/radius/http.go details(): the `row` object (all values are strings). */
export interface RadiusDetailsRow {
  username: string;
  client_name: string;
  package: string;
  mac_address: string;
  ip_address: string;
  mobile: string;
  address: string;
  expires_on: string;
  account_status: string;
  switch_name: string;
  last_synced: string;
  last_logoff: string;
}

/** GET /v1/api/radius/details?username=… response. */
export interface RadiusDetailsResponse {
  ok: boolean;
  row?: RadiusDetailsRow;
  /** Seconds since last_synced, or null if unknown. */
  synced_seconds_ago?: number | null;
  error?: string;
}

// ── Fleet status (dashboard / OLTs / routers) ──
// JSON siblings of the legacy /v1/frag/* HTML, added on the Go side
// alongside this React frontend. Shapes: internal/dashboard/api.go,
// internal/olts/api.go, internal/routers/api.go.

/** GET /v1/api/dashboard/tiles — the home-page overview counts. */
export interface DashboardTiles {
  routers_online: number;
  routers_total: number;
  routers_down: number;
  olts_online: number;
  olts_total: number;
  olts_down: number;
  sessions: number;
  issues: number;
}
export interface DashboardTilesResponse {
  ok: boolean;
  tiles?: DashboardTiles;
  error?: string;
}

/** A per-segment health summary (routers / OLTs). */
export interface FleetCount {
  online: number;
  total: number;
  down: number;
}
/** GET /v1/api/dashboard/poller — the navbar daemon-health widget. */
export interface DashboardPollerWidget {
  alive: boolean;
  beat_age_s: number;
  status: string; // "Operational" | "Degraded" | "Offline"
  routers: FleetCount;
  olts: FleetCount;
}
export interface DashboardPollerResponse {
  ok: boolean;
  poller?: DashboardPollerWidget;
  error?: string;
}

/** One OLT row from GET /v1/api/olts. The Telnet password is never sent;
 *  the other config fields (community/version/user/port) come along so the
 *  Add/Edit modal can prefill. */
export interface OltRow {
  id: number;
  name: string;
  ip: string;
  model: string;
  hardware_model?: string;
  enabled: boolean;
  telnet_status: string; // "up" | "down" | "unknown"
  issue_count: number;
  // Non-secret config (for the edit modal prefill).
  snmp_community: string;
  snmp_version: string;
  ssh_user: string;
  ssh_port: number;
  /** false → the poller has no recent data for this OLT (the *_? fields are absent). */
  live: boolean;
  status?: string;
  last_error?: string;
  onu_count?: number;
  onu_online?: number;
  cpu_percent?: number;
  temp_c?: string;
  firmware_version?: string;
  hardware_version?: string;
  olt_clock?: string;
  updated_at?: string; // RFC3339
}
export interface OltAggs {
  total: number;
  ok: number;
  down: number;
  disabled: number;
  unknown: number;
  enabled: number;
  online_onus: number;
  total_issues: number;
}
export interface OltsResponse {
  ok: boolean;
  rows: OltRow[];
  aggs: OltAggs;
  error?: string;
}
/** One ONU from GET /v1/api/olts/onus?olt=<name> — the cached snapshot the
 *  poller writes each cycle. Most fields are best-effort: `pon_port`/
 *  `onu_index`/`onu_name`/`username`/`is_online` are usually present; the
 *  optical/hardware/uptime fields appear only when the driver collected
 *  them this cycle. */
export interface OltOnu {
  olt?: string;
  olt_ip?: string;
  pon_port?: number;
  onu_index?: number;
  onu_name?: string;
  username?: string;
  is_online?: boolean;
  /** Unix-ts of the last cycle this ONU was seen active (had users behind
   *  it) — the oltissues "dead/decommissioned ONU" tracker, from Redis. */
  last_active?: number;
  serial?: string;
  model?: string;
  firmware?: string;
  rx_power_dbm?: string;
  tx_power_dbm?: string;
  temp_c?: string;
  distance_m?: number;
  uptime_seconds?: number;
  last_online?: string;
  last_offline?: string;
  dereg_reason?: string;
  lan_status?: string;
  lan_speed?: string;
  cpe_mac?: string;
  updated_at?: string;
}
export interface OltOnusResponse {
  ok: boolean;
  olt: string;
  onus: OltOnu[];
  error?: string;
}
/** One PON port in OltPanel.pon_ports. SFP fields are device-formatted
 *  strings ("45.79") and absent when the OLT has no SFP / no reading. */
export interface OltPonPort {
  port: number; // 1..N (the PON number)
  name?: string; // IF-MIB port label, e.g. "EPON0/1"
  alias?: string; // operator's ifDescr on the port (often blank)
  up: boolean; // ifOperStatus
  admin_up: boolean; // ifAdminStatus
  onu_online: number;
  onu_total: number;
  sfp_temp_c?: string;
  sfp_volt_v?: string;
  sfp_bias_ma?: string;
  sfp_tx_dbm?: string; // SFP transmit power
}
/** One GE/XGE uplink port in OltPanel.uplinks. SFP DDM fields are present
 *  only when an optical module is fitted in the cage (copper ports and empty
 *  cages have all of them absent). */
export interface OltUplinkPort {
  label: string; // "GE0/1", "XGE0/2", …
  alias?: string; // the operator's ifDescr ("Uplink-10G", …)
  up: boolean;
  admin_up: boolean;
  speed_mbps?: number; // ifHighSpeed
  sfp_temp_c?: string;
  sfp_volt_v?: string;
  sfp_bias_ma?: string;
  sfp_tx_dbm?: string; // SFP transmit power
  sfp_rx_dbm?: string; // SFP received power
}
/** GET /v1/api/olts/panel?olt=<name>'s `panel` — the front-panel snapshot
 *  the brand poller writes each cycle (all from Redis, no device poll).
 *  `psu1`/`psu2`: 0 = off/fault, 1 = on/ok, 2 = no-support (no such bay),
 *  -1 = the driver didn't read it. */
export interface OltPanel {
  olt: string;
  updated_at: string;
  pon_ports: OltPonPort[];
  uplinks: OltUplinkPort[];
  psu1: number;
  psu2: number;
  cpu_percent?: number;
  mem_percent?: number;
  board_temp_c?: string;
  cpu_temp_c?: string;
  uptime?: string;
  firmware?: string;
  hardware_version?: string;
  model?: string; // the device's own model string ("V1600D8", …)
}
export interface OltPanelResponse {
  ok: boolean;
  olt: string;
  panel: OltPanel | null;
  error?: string;
}
/** One registered OLT driver — GET /v1/api/cmd/olts/available_models. */
export interface OltModel {
  key: string; // matches olt_devices.model, e.g. "vsol_gpon"
  label: string; // dropdown text, e.g. "VSOL GPON"
  vendor: string; // brand name
  logo: string; // path under /assets/brand/olt/ (may be empty)
}
export interface OltModelsResponse {
  ok: boolean;
  models: OltModel[];
  error?: string;
}
/** POST /v1/api/olts/save — admin only. `id` 0 ⇒ create; >0 ⇒ update (ssh_pass omitted/empty ⇒ keep stored). */
export interface OltSaveRequest {
  id: number;
  name: string;
  ip: string;
  snmp_community: string;
  snmp_version: string; // "2c" | "3"
  model: string;
  ssh_user: string;
  ssh_pass?: string;
  ssh_port: number;
  enabled: boolean;
}
export interface OltSaveResult {
  ok: boolean;
  name?: string;
  error?: string;
}
export interface OltDeleteResult {
  ok: boolean;
  deleted?: number;
  name?: string;
  error?: string;
}

/** One router row from GET /v1/api/routers (slim — no connection creds). */
export interface RouterRow {
  id: number;
  name: string;
  ip: string;
  /** RouterOS API port (8728 plain / 8729 SSL). */
  port: number;
  /** RouterOS API username (not a secret; the password is never sent). */
  username: string;
  use_ssl: boolean;
  enabled: boolean;
  status: string; // "ok" | "unreachable" | "disabled" | "unknown"
  sessions: number;
  peak_today: number;
  peak_30d: number;
  err_count: number;
  err_avg?: string;
  live: boolean;
  cpu_load?: number;
  temp_c?: string;
  uptime?: string;
  last_error?: string;
  updated_at?: string; // RFC3339
}
export interface RouterAggs {
  total: number;
  ok: number;
  down: number;
  disabled: number;
  unknown: number;
  enabled: number;
  total_sessions: number;
  peak_today: number;
}
export interface RoutersResponse {
  ok: boolean;
  rows: RouterRow[];
  aggs: RouterAggs;
  error?: string;
}
/** POST /v1/api/routers/save — admin only. `id` 0 ⇒ create (password required); >0 ⇒ update (password omitted/empty ⇒ keep stored). */
export interface RouterSaveRequest {
  id: number;
  name: string;
  ip: string;
  port: number;
  username: string;
  password?: string;
  use_ssl: boolean;
  enabled: boolean;
}
export interface RouterSaveResult {
  ok: boolean;
  id?: number;
  name?: string;
  error?: string;
}
export interface RouterDeleteResult {
  ok: boolean;
  deleted?: number;
  name?: string;
  error?: string;
}

// ── Switches (the "browse by switch" picker + per-switch user list) ──
// JSON: internal/switches/domain.go (siblings of api/switches.php /
// api/switch_users.php).

/** One switch in the picker grid (GET /v1/api/switches/list). */
export interface SwitchSummary {
  switch_name: string;
  total: number;
  online_count: number;
  /** ONU online flag of the switch's own uplink box: true / false / null (no ONU data — show neutral). */
  onu_online: boolean | null;
}
export interface SwitchListResponse {
  ok: boolean;
  switches: SwitchSummary[];
  error?: string;
}

/** One subscriber configured behind a switch (GET /v1/api/switches/users?switch=…). */
export interface SwitchUserRow {
  username: string;
  client_name?: string;
  mobile?: string;
  ip_address: string;
  status: string; // live PPPoE: "online" | "offline" | …
  account_status: string; // RADIUS billing: "active" | "block" | "close"
  expires_on?: string; // raw "DD MMM YYYY" from MyBilling
  rx_power_dbm?: string;
}
export interface SwitchUsersResponse {
  ok: boolean;
  switch?: string;
  count?: number;
  rows?: SwitchUserRow[];
  /** Pass-through ONU JSON of the switch's own box; shape varies by brand. */
  onu?: unknown;
  error?: string;
}

// ── Action endpoints (the /v1/api/<feature>/run pattern) ──
// POST {action, args} → {ok, duration_ms, result?, error?}. `result` is
// the raw backend return value — shape depends on the action; callers
// narrow it. internal/radiusstatus/http.go: apiRun.

export interface RunRequest {
  action: string;
  args?: Record<string, unknown>;
}
export interface RunResponse<T = unknown> {
  ok: boolean;
  duration_ms?: number;
  result?: T;
  error?: string;
}

/** `result` of the `remove_mac_bind` action — internal/radius/mutate.go: RemoveMACBindResult. */
export interface RemoveMacBindResult {
  username: string;
  client_id: number;
  previous_mac: string;
  ok: boolean;
  portal_message?: string;
}

/** internal/radius/domain.go: BillingSummary — the totals row of the ledger report. */
export interface BillingSummary {
  total: number;
  collected: number;
  adjustment: number;
  discount: number;
  due: number;
  currency: string;
  period: string;
  invoices?: number;
}
/** `result` of the `ledger` action — internal/radius/domain.go: LedgerFull.
 *  `html` is the show_ledger_rpt.php detail table, server-sanitised
 *  (classes/styles stripped). Slow (~17s portal fetch) — call on demand. */
export interface LedgerFull {
  summary: BillingSummary;
  html: string;
  period: string;
}

/** `result` of the `update_client` action — internal/radius/clientform.go: UpdateClientResult. */
export interface UpdateClientResult {
  username: string;
  client_id: number;
  ok: boolean;
  portal_message?: string;
  mobile_changed?: boolean;
  prev_mobile?: string;
  new_mobile?: string;
  switch_changed?: boolean;
  prev_switch_id?: string;
  new_switch_id?: string;
  new_switch_name?: string;
}

// ── RADIUS Status & Config page (GET /v1/api/radius_status/state) ──
// Hand-shaped snake_case DTO in internal/radiusstatus/http.go: stateDTO.
// The radiusbackend Descriptor/Action/ActionInput types carry no json
// tags on the Go side, so these mirror the manual DTO, not the structs.

/** Rolling-window health verdict — internal/radiusbackend/health.go. */
export type RadiusHealth = "ok" | "degraded" | "broken" | "unknown";

/** internal/radiusbackend/backend.go: Descriptor (DTO'd). Empty `key` ⇒ no backend active. */
export interface RadiusBackendDescriptor {
  key: string;
  label: string;
  kind: string; // "scraper" | "api"
  vendor: string;
}

/** internal/radiusbackend/backend.go: ActionInput (DTO'd). */
export interface RadiusActionInput {
  name: string;
  label: string;
  type: string; // "text" | "number" | "mac" | "bool"
  required: boolean;
}

/** internal/radiusbackend/backend.go: Action (DTO'd). */
export interface RadiusActionDescriptor {
  key: string;
  label: string;
  kind: string; // "read" | "write"
  description: string;
  inputs: RadiusActionInput[];
}

/** internal/radiusbackend/backend.go: CallRecord — one telemetry row, newest-first in `history`. */
export interface RadiusCallRecord {
  at: string; // RFC3339
  ok: boolean;
  dur_ms: number;
  err?: string;
}

/** Connection card. `last_checked` is null when never pinged. */
export interface RadiusConnectionStatus {
  health: RadiusHealth;
  last_checked: string | null;
  last_error: string;
  history: RadiusCallRecord[];
  ok_count: number;
  window: number;
}

/** Bulk-sync card — mirrors the radiussync ticker's Prometheus gauges. */
export interface RadiusBulkSyncStatus {
  last_run_at: string | null;
  last_run_ok: boolean;
  last_success_at: string | null;
  last_failure_at: string | null;
  last_duration_ms: number;
  rows: number;
  macs_found: number;
  macs_missing: number;
  macs_errored: number;
  next_run_at: string | null;
  health: RadiusHealth;
  history: RadiusCallRecord[];
  history_ok: number;
  window: number;
}

/** One recent online-payment kick — internal/radiusstatus/online_payment_kicker.go: RecentKick. */
export interface RadiusRecentKick {
  at: string;
  username: string;
  client_name: string;
  address: string;
  router: string;
  amount_bdt: number;
  gateway: string;
  ok: boolean;
  err?: string;
}

/** Online-payment kicker card — OnlinePaymentKickerStatus (pass-through, has its own json tags).
 *  Note: zero `time.Time` values serialize as "0001-01-01T00:00:00Z" (not null) — treat year < 2000 as "never". */
export interface RadiusKickerStatus {
  last_run_at: string;
  last_duration_ns: number;
  last_run_ok: boolean;
  last_error?: string;
  last_rows_scanned: number;
  last_rows_kicked: number;
  last_rows_errored: number;
  next_run_at: string;
  kicks_today: number;
  kicks_today_date: string;
  recent: RadiusRecentKick[];
}

/** One row of the per-action telemetry table. */
export interface RadiusActionStatus {
  action: RadiusActionDescriptor;
  health: RadiusHealth;
  last_called_at: string | null;
  last_error: string;
  last_error_at: string | null;
  latest_is_failure: boolean;
  history: RadiusCallRecord[];
  history_ok: number;
  window: number;
}

/** The whole RADIUS Status page view-model. */
export interface RadiusStatusState {
  active_backend: RadiusBackendDescriptor;
  available_backends: RadiusBackendDescriptor[];
  base_url: string;
  username: string;
  connection: RadiusConnectionStatus;
  bulk_sync: RadiusBulkSyncStatus;
  online_payment_kicker: RadiusKickerStatus;
  actions: RadiusActionStatus[];
}
export interface RadiusStatusStateResponse {
  ok: boolean;
  state?: RadiusStatusState;
  error?: string;
}

/** POST /v1/api/radius_status/save body — admin only. `password` omitted/empty ⇒ keep stored. */
export interface RadiusSaveRequest {
  backend_kind: string;
  base_url: string;
  username: string;
  password?: string;
  extra?: Record<string, unknown>;
}
export interface RadiusSaveResult {
  ok: boolean;
  error?: string;
}

/** POST /v1/api/radius_status/test — connectivity probe, optionally against unsaved creds. */
export interface RadiusTestResult {
  ok: boolean;
  duration_ms?: number;
  error?: string;
}

// ── IP Phone Gateway page (GET /v1/api/ipphone/state) ──
// Hand-shaped snake_case DTO in internal/ipphonestatus/http.go: stateDTO.
// Multi-instance: one card per row in MySQL `ipphone_gateway`. The
// ipphone Descriptor/Action/ActionInput types carry no json tags on the
// Go side, and the row's decrypted password is never serialized — so
// these mirror the DTO, not the structs. CallRecord + ServiceStatus
// already have snake_case tags and pass through.

/** Same rolling-10 / 3-in-a-row verdict as the RADIUS health rule. */
export type IPPhoneHealth = "ok" | "degraded" | "broken" | "unknown";

/** internal/ipphone/gateway.go: Descriptor (DTO'd). Empty `key` ⇒ unknown kind. */
export interface IPPhoneKindDescriptor {
  key: string;
  label: string;
  kind: string; // "api" | "ami" | "cloud" | "scraper"
  vendor: string;
}

/** internal/ipphone/gateway.go: ActionInput (DTO'd). */
export interface IPPhoneActionInput {
  name: string;
  label: string;
  type: string; // "text" | "number" | "tel" | "textarea"
  required: boolean;
}

/** internal/ipphone/gateway.go: Action (DTO'd). */
export interface IPPhoneActionDescriptor {
  key: string;
  label: string;
  kind: string; // "read" | "write"
  description: string;
  inputs: IPPhoneActionInput[];
}

/** internal/ipphone/gateway.go: CallRecord — one telemetry row, newest-first in `history`. */
export interface IPPhoneCallRecord {
  at: string; // RFC3339
  ok: boolean;
  dur_ms: number;
  err?: string;
}

/** Per-gateway Connection block. `last_*` are null when never recorded. */
export interface IPPhoneConnectionStatus {
  health: IPPhoneHealth;
  last_checked: string | null;
  last_error: string;
  last_error_at: string | null;
  latest_is_fail: boolean;
  history: IPPhoneCallRecord[];
  ok_count: number;
  window: number;
}

/** One row of a gateway card's Actions table. */
export interface IPPhoneActionStatus {
  action: IPPhoneActionDescriptor;
  health: IPPhoneHealth;
  last_called_at: string | null;
  last_error: string;
  last_error_at: string | null;
  latest_is_failure: boolean;
  history: IPPhoneCallRecord[];
  history_ok: number;
  window: number;
}

// — Service-status payload shapes (the aggregator's 3 sections). These
//   mirror the decoder structs in internal/ipphonestatus/http.go. —
export interface IPPhoneSipTrunk {
  index: number;
  trunk_name: string;
  address: string;
  transport: string;
  state: string;
  status: string;
  registered: boolean;
  up: boolean;
  calls_in: number;
  calls_in_fail: number;
  calls_out: number;
  calls_out_fail: number;
}
export interface IPPhoneSipTrunks {
  total_count: number;
  registered_count: number;
  up_count: number;
  trunks: IPPhoneSipTrunk[];
}
export interface IPPhoneVolte {
  configured: boolean;
  registered: boolean;
  module: string;
  sim_card: string;
  operator: string;
  mode: string;
  signal_bars: number;
  data_status: string;
  ip_address: string;
}
export interface IPPhonePerf {
  model: string;
  sn: string;
  firmware: string;
  uptime_seconds: number;
  mem_total_kb: number;
  fs_total_kb: number;
  cpu_percent: number;
  mem_used_kb: number;
  mem_percent: number;
  fs_used_kb: number;
  fs_percent: number;
}
/** One readout of the Service-status panel — verdict + raw payload (absent ⇒ fetch failed this cycle). */
export interface IPPhoneSection<P> {
  health: IPPhoneHealth;
  error?: string;
  payload?: P;
}
/** internal/ipphonestatus/servicestatus.go: ServiceStatus — the 60-s aggregator snapshot. */
export interface IPPhoneServiceStatus {
  gateway_id: number;
  updated_at: string;
  health: IPPhoneHealth;
  sip: IPPhoneSection<IPPhoneSipTrunks>;
  volte: IPPhoneSection<IPPhoneVolte>;
  perf: IPPhoneSection<IPPhonePerf>;
}

/** One gateway card — the persisted row (sans password) + live state. */
export interface IPPhoneGatewayCard {
  id: number;
  name: string;
  gateway_kind: string;
  host: string;
  username: string;
  extra: Record<string, unknown>;
  priority: number;
  enabled: boolean;
  updated_at: string | null;
  updated_by: string;
  /** Resolved registry descriptor; empty `key` ⇒ the kind isn't compiled in. */
  descriptor: IPPhoneKindDescriptor;
  /** true when the row built into a live backend (enabled + buildable). */
  configured: boolean;
  connection: IPPhoneConnectionStatus;
  /** null until the aggregator's first round lands. */
  service: IPPhoneServiceStatus | null;
  /** empty when there's no live backend (disabled / unknown kind / bad creds). */
  actions: IPPhoneActionStatus[];
}

export interface IPPhoneState {
  available_kinds: IPPhoneKindDescriptor[];
  gateways: IPPhoneGatewayCard[];
}
export interface IPPhoneStateResponse {
  ok: boolean;
  state?: IPPhoneState;
  error?: string;
}

/** POST /v1/api/ipphone/save — admin only. `id` 0 ⇒ INSERT; `password` omitted/empty ⇒ keep stored (UPDATE) / error (INSERT). */
export interface IPPhoneSaveRequest {
  id: number;
  name: string;
  gateway_kind: string;
  host: string;
  username: string;
  password?: string;
  extra?: Record<string, unknown>;
  priority: number;
  enabled?: boolean;
}
export interface IPPhoneSaveResult {
  ok: boolean;
  id?: number;
  error?: string;
}

/** POST /v1/api/ipphone/test — probe connectivity, optionally against unsaved creds. */
export interface IPPhoneTestResult {
  ok: boolean;
  duration_ms?: number;
  error?: string;
}

/** POST /v1/api/ipphone/run — `gateway_id` 0/omitted routes to the lowest-priority gateway supporting `action`. */
export interface IPPhoneRunRequest {
  gateway_id?: number;
  action: string;
  args?: Record<string, unknown>;
}
export interface IPPhoneRunResult {
  ok: boolean;
  duration_ms?: number;
  gateway_id?: number;
  gateway_name?: string;
  result?: unknown;
  error?: string;
}

/** POST /v1/api/ipphone/delete — admin only. */
export interface IPPhoneDeleteResult {
  ok: boolean;
  deleted?: number;
  error?: string;
}

// ── Call Blasting page (GET /v1/api/callblast/state) ──
// Hand-shaped snake_case DTO in internal/callblaststatus/http.go:
// cbStateDTO. PageData / callblast Descriptor carry no json tags on the
// Go side; VoiceEntry/SenderEntry/ReportEntry/Subscriber/BatchResult/
// BlastGroupResult already do and pass through.

export type CallBlastHealth = "ok" | "degraded" | "broken" | "unknown";

export interface CallBlastBackendDescriptor {
  key: string;
  label: string;
  kind: string; // "api"
  vendor: string;
}

/** A voice template registered with the provider (only `status:"approved"` are dialable). */
export interface CallBlastVoice {
  id: number;
  name: string;
  status: string;
}
/** A caller-ID / sender registered with the provider (only `status:"active"` are usable). */
export interface CallBlastSender {
  id: number;
  callingNumber: string;
  status: string;
}
/** One row of the "Recent broadcasts" table — a slim projection of the provider's report. */
export interface CallBlastReport {
  id: number;
  name: string;
  status: string;
  createdAt: string;
}
/** A planned blast batch — preview before Send (the provider caps each broadcast at `batch_size`). */
export interface CallBlastBatchPlanEntry {
  index: number;
  count: number;
}
/** This-calendar-month activity (count + reach only — the provider's API exposes no cost). */
export interface CallBlastMonthly {
  group_count: number;
  broadcast_count: number;
  total_numbers: number;
}
export interface CallBlastConnection {
  health: CallBlastHealth;
  last_error: string;
}

/** The whole Call Blasting page view-model. */
export interface CallBlastState {
  configured: boolean;
  backend_key: string;
  backend_label: string;
  backend_vendor: string;
  base_url: string;
  updated_at: string | null;
  updated_by: string;
  available: CallBlastBackendDescriptor[];
  /** Live account balance, or null if it couldn't be fetched (see `balance_err`). */
  balance: number | null;
  balance_err: string;
  voices: CallBlastVoice[];
  senders: CallBlastSender[];
  /** All active subscribers expiring this calendar month (valid + invalid). */
  total_count: number;
  /** Subscribers with a dialable mobile — the blast recipient count. */
  valid_count: number;
  /** Subscribers whose mobile needs fixing (see the needs-fix endpoint). */
  invalid_count: number;
  batch_plan: CallBlastBatchPlanEntry[];
  /** Per-broadcast recipient cap the provider enforces. */
  batch_size: number;
  recent: CallBlastReport[];
  monthly: CallBlastMonthly;
  generated_at: string | null;
  connection: CallBlastConnection;
}
export interface CallBlastStateResponse {
  ok: boolean;
  state?: CallBlastState;
  error?: string;
}

/** One subscriber whose `mobile` value can't be used for a blast. */
export interface CallBlastSubscriber {
  username: string;
  client_name: string;
  /** Normalised when `valid`, else same as `mobile_raw`. */
  mobile: string;
  /** Original DB value. */
  mobile_raw: string;
  package: string;
  expires_on: string; // raw "DD MMM YYYY"
  status: string;
  valid: boolean;
  /** "missing" | "too_short" | "too_long" | "bad_prefix" | "junk" | "" when valid. */
  reason?: string;
}
export interface CallBlastNeedsFixResponse {
  ok: boolean;
  subscribers?: CallBlastSubscriber[];
  total?: number;
  /** Per-reason counts, e.g. `{ missing: 331, too_short: 63 }`. */
  reason_counts?: Record<string, number>;
  generated_at?: string;
  error?: string;
}

/** POST /v1/api/callblast/save — admin only. `token` omitted/empty ⇒ keep stored. */
export interface CallBlastSaveRequest {
  backend_kind: string;
  base_url: string;
  token?: string;
  extra?: Record<string, unknown>;
}
export interface CallBlastSaveResult {
  ok: boolean;
  error?: string;
}
/** POST /v1/api/callblast/test — connectivity probe, optionally against unsaved creds. */
export interface CallBlastTestResult {
  ok: boolean;
  duration_ms?: number;
  error?: string;
}
/** POST /v1/api/callblast/run — the action front-door (get_balance, list_voices, …). */
export interface CallBlastRunRequest {
  action: string;
  args?: Record<string, unknown>;
}
export interface CallBlastRunResult {
  ok: boolean;
  duration_ms?: number;
  result?: unknown;
  error?: string;
}

/** One submitted blast batch — internal/callblaststatus/service.go: BatchResult. */
export interface CallBlastBatchResult {
  index: number;
  broadcast_id: number;
  count: number;
  duration_ms: number;
  ok: boolean;
  error?: string;
  /** Phone numbers the provider rejected as duplicates and we stripped + retried. */
  skipped_duplicates?: string[];
  /** true ⇒ we hit the dup-retry cap and gave up on this batch. */
  dup_retry_exhausted?: boolean;
}
/** The outcome of one StartBlastAll run — internal/callblaststatus/service.go: BlastGroupResult. */
export interface CallBlastGroupResult {
  voice: string;
  sender: string;
  /** Unique numbers after dedup. */
  total_numbers: number;
  /** Rows dropped because the same phone was registered to multiple usernames. */
  intra_list_dup: number;
  batch_count: number;
  success_count: number;
  duration_ms: number;
  batches: CallBlastBatchResult[];
}
/** POST /v1/api/callblast/start_blast_all — server pulls the recipient set from radius_subscribers itself. */
export interface CallBlastStartResponse {
  ok: boolean;
  result?: CallBlastGroupResult;
  error?: string;
}

// ── SMS & Calls page (GET /v1/api/sms/main) ──
// internal/sms/domain.go — these mirror the Go structs' json tags directly.

/** Top-of-page KPI tiles. `*_pct` = today vs the 7-day daily average (capped 100). */
export interface SmsKpis {
  sms_today: number;
  calls_today: number;
  missed_today: number;
  sms_avg7: number;
  calls_avg7: number;
  sms_pct: number;
  calls_pct: number;
  missed_pct: number;
}
/** A human timestamp: short label + ISO tooltip. */
export interface SmsStamp {
  label: string; // "2m ago" / "in 3h" / "Apr 28, 14:10"
  title: string; // "2026-04-30 13:55:24"
}
/** One row of the "Missed calls · 24h" card (aggregated by caller-tail). */
export interface SmsMissedCall {
  caller: string; // raw column value (DOM id seed)
  caller_fmt: string; // normalised display
  subscriber?: string; // PPPoE username if the mobile matches
  // Subscriber context (only when `subscriber` matched), from radius_subscribers.
  client_name?: string;
  account_status?: string; // "active" | "block" | "close" | ""
  package?: string;
  /** Saved-contacts name — only set when there's no `subscriber` match. */
  contact_name?: string;
  miss_count: number; // zero-duration attempts
  rang: boolean; // we called back, no answer
  start_time: string; // raw MySQL DATETIME
  when: SmsStamp;
}
/** One row of the "Last 20 SMS" list. */
export interface SmsItem {
  contact: string; // raw stored value
  contact_fmt: string; // normalised display
  name?: string; // matched contact name
  direction: "in" | "out";
  body: string;
  datetime: string; // raw MySQL DATETIME
  when: SmsStamp;
  replied: boolean; // an op replied after this message
}
/** internal/sms/domain.go: PageData. */
export interface SmsPageData {
  kpis: SmsKpis;
  missed: SmsMissedCall[];
  sms: SmsItem[];
}
export interface SmsMainResponse {
  ok: boolean;
  data?: SmsPageData;
  error?: string;
}

// ── Contacts (the compose autocomplete + contacts modal) ──
// internal/contacts — GET/POST /v1/api/contacts, DELETE /v1/api/contacts?id=N.

/** One saved contact. `number` is the 11-digit `01XXXXXXXXX`. */
export interface Contact {
  id: number;
  name: string;
  number: string;
}
export interface ContactsListResponse {
  ok: boolean;
  rows: Contact[];
  count: number;
  error?: string;
}
export interface ContactAddResponse {
  ok: boolean;
  id?: number;
  name?: string;
  number?: string;
  error?: string;
}
export interface ContactRemoveResponse {
  ok: boolean;
  deleted?: number;
  error?: string;
}

// ── Portal users (admin only) — GET/POST /v1/api/users[/save|/delete] ──
// internal/auth/usercrud.go — the React port of /users.php.

export type PortalRole = "admin" | "support" | "reseller";

/** One portal sign-in account. */
export interface PortalUser {
  id: number;
  username: string;
  full_name: string;
  role: PortalRole;
  enabled: boolean;
  last_login: string | null; // RFC3339, null if never
  created_at: string | null; // RFC3339, null if unset
  reseller_id: number | null; // null unless role === "reseller"
  reseller_name?: string; // joined name; "" / absent if the id no longer exists in radius_resellers
  /** Per-reseller device grants (role === "reseller" only) — names of the granted OLTs / routers. */
  grant_olt_names?: string[];
  grant_router_names?: string[];
}
export interface PortalUsersListResponse {
  ok: boolean;
  rows: PortalUser[];
  count: number;
  error?: string;
}
/** POST /v1/api/users/save — `id` 0 ⇒ create (username + password required); >0 ⇒ update
 *  (username is immutable / ignored; `password` omitted/empty ⇒ keep stored). Admin only. */
export interface PortalUserSaveRequest {
  id: number;
  username: string;
  full_name: string;
  role: PortalRole;
  /** Required when role === "reseller" (a valid radius_resellers.id); ignored otherwise. */
  reseller_id?: number | null;
  enabled: boolean;
  password?: string;
  /** Per-reseller device grants — applied only when role === "reseller" (cleared otherwise).
   *  Device IDs from the OLT / router fleet lists (GET /v1/api/olts, /v1/api/routers). */
  grant_olts?: number[];
  grant_routers?: number[];
}

// ── Resellers roster — GET /v1/api/resellers/list (internal/resellers) ──

/** One reseller from the synced radius_resellers roster (Company → Reseller List). */
export interface ResellerRosterRow {
  id: number;
  name: string;
  mobile?: string;
  bill_start?: string;
  client_count: number;
  last_synced?: string;
}
export interface ResellersListResponse {
  ok: boolean;
  resellers: ResellerRosterRow[];
  count: number;
  error?: string;
}
export interface PortalUserSaveResult {
  ok: boolean;
  id?: number;
  username?: string;
  error?: string;
}
/** POST /v1/api/users/delete — admin only. Can't delete yourself or the only enabled admin. */
export interface PortalUserDeleteResult {
  ok: boolean;
  deleted?: number;
  username?: string;
  error?: string;
}

// ── Messenger / WhatsApp inbox — GET/POST /v1/api/messenger/* ──
// internal/messengerstatus/json.go (the hand-shaped JSON siblings of
// the HTML fragments) + the existing JSON action endpoints. Secrets are
// never serialized.

/** One conversation row (PSID / WA threading id), DTO'd. */
export interface MessengerConversation {
  id: number;
  channel: "messenger" | "whatsapp";
  external_id: string;
  display_name: string;
  profile_pic_url: string;
  phone_e164: string;
  phone_last11: string;
  /** PPPoE username if the phone matched `radius_subscribers.mobile`. */
  subscriber_username: string;
  /** 0 = unassigned; else the rep's portal_users id. */
  assigned_uid: number;
  state: "open" | "closed" | "snoozed";
  last_message_at: string | null;
  last_inbound_at: string | null;
  unread_count: number;
  created_at: string | null;
}
/** A conversation + the small formatted bits the list/header need. */
export interface MessengerConversationView {
  conv: MessengerConversation;
  channel_label: string; // "Messenger" | "WhatsApp"
  channel_icon: string; // Tabler icon class (no "ti " prefix) — informational
  phone_pretty: string; // "01XXXXXXXXX" or ""
  rel_time: string; // "5m ago"
  assignee_username: string; // "" when unassigned
}
/** One message in a thread, DTO'd. */
export interface MessengerMessage {
  id: number;
  direction: "in" | "out";
  external_msg_id: string;
  text: string;
  attachments: string; // raw JSON or ""
  sender_kind: "customer" | "agent" | "echo" | "system";
  sent_by_uid: number;
  sender_username: string; // rep username for agent rows; "" otherwise
  sent_at: string | null;
  inbound: boolean;
  echo: boolean;
  time: string; // "14:35" (Asia/Dhaka, same-day)
  title: string; // full timestamp
}

/** GET /v1/api/messenger/bubble — the floating-bubble payload. */
export interface MessengerBubbleResponse {
  ok: boolean;
  unread_total: number;
  recent: MessengerConversationView[];
  /** The current rep's UID — compare to `conv.assigned_uid` for claim-vs-take-over. */
  viewer_uid: number;
  generated_at: string | null;
  error?: string;
}
/** GET /v1/api/messenger/conversation?id=N — one thread. */
export interface MessengerConversationResponse {
  ok: boolean;
  view: MessengerConversationView;
  messages: MessengerMessage[];
  viewer_uid: number;
  /** false for WhatsApp — reply via the FB Page Inbox app instead. */
  outbound_supported: boolean;
  error?: string;
}

/** GET /v1/api/messenger/state — the admin config-card data. */
export interface MessengerStateResponse {
  ok: boolean;
  configured: boolean;
  backend_key: string;
  app_id: string;
  page_id: string;
  updated_at: string | null;
  updated_by: string;
  connection: { ok: boolean; error: string; duration_ms: number };
  error?: string;
}

/** POST /v1/api/messenger/save — admin only. Secret fields omitted/empty ⇒ keep stored. */
export interface MessengerSaveRequest {
  app_id: string;
  page_id: string;
  app_secret?: string;
  page_token?: string;
  verify_token?: string;
  extra?: Record<string, unknown>;
}
export interface MessengerSaveResult {
  ok: boolean;
  warning?: string;
  error?: string;
}
export interface MessengerTestResult {
  ok: boolean;
  duration_ms?: number;
  error?: string;
}
/** One canned-response template row (id 0 ⇒ unsaved/new). */
export interface MessengerTemplate {
  id: number;
  label: string;
  body: string;
  updated_at: string;
}
/** GET /v1/api/messenger/templates — admin|support. */
export interface MessengerTemplateListResponse {
  ok: boolean;
  templates?: MessengerTemplate[];
  error?: string;
}
/** POST /v1/api/messenger/templates/save — admin only. id=0 ⇒ INSERT. */
export interface MessengerTemplateSaveResponse {
  ok: boolean;
  id?: number;
  error?: string;
}
/** POST /v1/api/messenger/send — admin|support. `whatsapp_outbound_unsupported` error ⇒ reply via Page Inbox. */
export interface MessengerSendResult {
  ok: boolean;
  message_id?: number;
  error?: string;
  hint?: string;
}
/** POST /v1/api/messenger/claim — admin|support. `claimed:false` ⇒ someone else holds it (`assigned_username`). */
export interface MessengerClaimResult {
  ok: boolean;
  claimed?: boolean;
  assigned_uid?: number;
  assigned_username?: string;
  error?: string;
}
export interface MessengerOpResult {
  ok: boolean;
  prev_uid?: number;
  assigned_uid?: number;
  error?: string;
}

/** Thrown by the client for any non-2xx response. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** Parsed JSON body if the server sent one (often `{error: "..."}`). */
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// ── Bulk SMS page (GET /v1/api/bulksms/state) ──
// Backend: internal/bulksmsstatus/http.go. Page lives at /bulk-sms.
export interface BulkSmsBackend {
  key: string;
  label: string;
  kind: string;
  vendor: string;
}
export interface BulkSmsHealth {
  state: "ok" | "degraded" | "broken" | "unknown";
  color: string;
  history_count: number;
  last_error: string;
}
export interface BulkSmsState {
  configured: boolean;
  backend_key: string;
  username: string;
  /** Optional convenience: pre-fills the Compose form's Sender ID
   *  field for transactional sends so the operator doesn't retype
   *  the same number every time. Per-send overrides win;
   *  promotional sends ignore this (campaigns lock their own sender). */
  default_sender_id: string;
  updated_at: string | null;
  updated_by: string;
  available: BulkSmsBackend[];
  health: BulkSmsHealth;
}
export interface BulkSmsStateResponse {
  ok: boolean;
  state?: BulkSmsState;
  error?: string;
}
/** POST /v1/api/bulksms/save — admin only. Account credentials plus
 *  the default Sender ID (a convenience that pre-fills the compose
 *  form). Omit/empty api_key to keep the stored ciphertext. */
export interface BulkSmsSaveRequest {
  backend_kind?: string;
  username: string;
  api_key?: string;
  default_sender_id?: string;
  extra?: Record<string, unknown>;
}
export interface BulkSmsSaveResponse {
  ok: boolean;
  warning?: string;
  error?: string;
}
export interface BulkSmsTestResponse {
  ok: boolean;
  duration_ms?: number;
  error?: string;
}
export interface BulkSmsBalanceResponse {
  ok: boolean;
  credit?: string;
  duration_ms?: number;
  error?: string;
}
/** POST /v1/api/bulksms/send — admin OR support. */
export interface BulkSmsSendRequest {
  label?: string;
  body: string;
  recipients: string;
  txn_type?: string;
  campaign_id?: string;
  campaign_name?: string;
  sender_id?: string;
}
export interface BulkSmsSendResult {
  job_id: number;
  txn_id: string;
  total: number;
  sent: number;
  failed: number;
  invalid_parsed: string[];
}
export interface BulkSmsSendResponse {
  ok: boolean;
  result?: BulkSmsSendResult;
  error?: string;
}
/** One row in the job history grid. */
export interface BulkSmsJob {
  id: number;
  label: string;
  txn_type: string;
  sender_id: string;
  campaign_id: string;
  campaign_name: string;
  body_preview: string;
  total: number;
  sent: number;
  failed: number;
  delivered: number;
  state: "queued" | "running" | "done" | "failed" | string;
  txn_id: string;
  err_text: string;
  created_by: number;
  created_at: string;
  finished_at: string | null;
}
export interface BulkSmsJobsResponse {
  ok: boolean;
  jobs?: BulkSmsJob[];
  error?: string;
}
export interface BulkSmsJobMessage {
  id: number;
  mobile: string;
  tracking_id: string;
  sms_count: number;
  lang_type: string;
  res_code: string;
  res_text: string;
  dlr_code: string;
  operator_status: string;
  state: "pending" | "sent" | "failed" | "delivered" | string;
  created_at: string;
}
export interface BulkSmsJobDetailResponse {
  ok: boolean;
  job?: BulkSmsJob;
  messages?: BulkSmsJobMessage[];
  error?: string;
}
/** Reserved for a future Phase-3-style canned-template endpoint
 *  (not implemented yet — kept here so the React page can flag where
 *  the future schema would land). */
export interface BulkSmsTemplate {
  id: number;
  label: string;
  body: string;
}

/** GET /v1/api/bulksms/recipients?filter=active|expiring|all — one row
 *  per matching subscriber, mobile normalised to 880XXXXXXXXX when
 *  Valid is true. */
export interface BulkSmsRecipient {
  username: string;
  client_name: string;
  mobile: string;
  mobile_raw: string;
  account_status: string;
  expires_on: string;
  valid: boolean;
  reason?: string;
}
export type BulkSmsRecipientFilter = "active" | "expiring" | "all";
export interface BulkSmsRecipientsResponse {
  ok: boolean;
  filter?: BulkSmsRecipientFilter | string;
  total?: number;
  valid?: number;
  invalid?: number;
  recipients?: BulkSmsRecipient[];
  error?: string;
}

/** One row of bulksms_campaigns — a BTRC-pre-approved MiMSMS
 *  promotional campaign. The (campaign_id, sender_id, approved_body)
 *  triple is locked together at approval time; promotional sends must
 *  use all three verbatim or BTRC rejects/fines. */
export interface BulkSmsCampaign {
  id: number;
  label: string;
  campaign_id: string;
  sender_id: string;
  approved_body: string;
  created_by: number;
  created_at: string;
  updated_at: string;
}
export interface BulkSmsCampaignListResponse {
  ok: boolean;
  campaigns?: BulkSmsCampaign[];
  error?: string;
}
export interface BulkSmsCampaignSaveResponse {
  ok: boolean;
  id?: number;
  error?: string;
}
